import { useMemo, type ReactNode } from "react"

import { type ColumnDef } from "@coval/ui"

import { EntityPickerDialog } from "@/components/entity-picker/EntityPickerDialog"
import type { TestCaseOption } from "@/types_api/backend/transport/list_test_cases_transport_response"
import type { TestSetRecord } from "@/types/api/TestSetRecord"

import { groupTestCaseSelection } from "../utils/testCaseSelection"
import { useTestCaseLibraryPickerProps } from "./TestCaseLibraryPickerProps"
import { useTestSetPickerDialogProps } from "./TestSetPickerDialog"

type PickerRow =
  | (TestSetRecord & { kind: "sets"; record: TestSetRecord })
  | (TestCaseOption & { kind: "cases"; record: TestCaseOption })

export function TestSelectionPickerDialog({
  isOpen,
  onCancel,
  view,
  banner,
  testSets,
  isLoading,
  draftSets,
  onSetsChange,
  draftCases,
  onCasesChange,
  onApplySets,
  onApplyCases,
  singleSelect,
  tagColorMap,
  createAction,
}: {
  isOpen: boolean
  onCancel: () => void
  view: string
  banner: ReactNode
  testSets: TestSetRecord[]
  isLoading: boolean
  draftSets: string[]
  onSetsChange: (ids: string[]) => void
  draftCases: string[]
  onCasesChange: (ids: string[]) => void
  onApplySets: () => void
  onApplyCases: (ids: string[], testSetIds: string[]) => void
  singleSelect?: boolean
  tagColorMap?: Record<string, string | null | undefined>
  createAction?: ReactNode
}) {
  const sets = useTestSetPickerDialogProps({
    isOpen,
    onCancel,
    onApply: onApplySets,
    testSets,
    isLoading,
    selectedIds: draftSets,
    onSelectionChange: onSetsChange,
    allowMultiple: !singleSelect,
    tagColorMap,
    createAction,
  })
  const cases = useTestCaseLibraryPickerProps({
    selectedIds: draftCases,
    onSelectionChange: onCasesChange,
    singleSelect,
    enabled: isOpen && view === "cases",
  })
  const items = useMemo<PickerRow[]>(
    () =>
      view === "sets"
        ? sets.items.map((record) => ({ ...record, kind: "sets", record }))
        : cases.items.map((record) => ({ ...record, kind: "cases", record })),
    [view, sets.items, cases.items],
  )
  const columns = useMemo<ColumnDef<PickerRow>[]>(
    () =>
      view === "sets"
        ? sets.columns.map((column) => ({
            ...column,
            accessorKey: undefined,
            cell: (row) =>
              row.kind === "sets" ? column.cell?.(row.record) : null,
          }))
        : cases.columns.map((column) => ({
            ...column,
            accessorKey: undefined,
            cell: (row) =>
              row.kind === "cases" ? column.cell?.(row.record) : null,
          })),
    [view, sets.columns, cases.columns],
  )

  return (
    <EntityPickerDialog<PickerRow>
      {...(view === "sets" ? sets : cases)}
      isOpen={isOpen}
      onCancel={onCancel}
      onApply={() => {
        if (view === "sets") {
          onApplySets()
        } else {
          const grouped = groupTestCaseSelection(draftCases, cases.items)
          onApplyCases(grouped.selectedIds, grouped.testSetIds)
        }
      }}
      catalogKey={view}
      items={items}
      columns={columns}
      getItemId={(row) => row.id}
      getItemLabel={(row) =>
        row.kind === "sets"
          ? sets.getItemLabel(row.record)
          : cases.getItemLabel(row.record)
      }
      matchesSearch={(row, query) =>
        row.kind === "sets"
          ? sets.matchesSearch(row.record, query)
          : cases.matchesSearch(row.record, query)
      }
      rowAttributes={(row) =>
        row.kind === "sets"
          ? sets.rowAttributes(row.record)
          : cases.rowAttributes(row.record)
      }
      isSelectionDisabled={(row) =>
        row.kind === "cases" && cases.isSelectionDisabled(row.record)
      }
      reconcileSelection={
        view === "cases" ? cases.reconcileSelection : undefined
      }
      applyDisabled={view === "cases" && cases.applyDisabled}
      allowMultiple={view === "cases" || !singleSelect}
      defaultSort={view === "sets" ? sets.defaultSort : undefined}
      banner={
        <>
          {banner}
          {view === "cases" && cases.banner}
        </>
      }
    />
  )
}
